/**
 * Mode-Aware API Client for Storybook API
 *
 * Supports three operational modes:
 * - Development Mode: X-User-Id header (no auth required)
 * - Debug Mode: JWT tokens with Cognito test users
 * - Production Mode: JWT tokens with real OAuth
 */

import { fetchAuthSession } from 'aws-amplify/auth';

const DEBUG = process.env.NODE_ENV !== 'production';

const log = (...args) => {
  if (DEBUG) console.log('[API CLIENT]', ...args);
};

// Global error handler for showing toast notifications
let globalErrorHandler = null;

export const setApiErrorHandler = (handler) => {
  globalErrorHandler = handler;
  log('Global API error handler registered');
};

/**
 * Mode detection based on environment variables
 * Made dynamic to support testing
 */
const MODE = {
  get isDevelopment() {
    return process.env.REACT_APP_DEV_MODE === 'true';
  },
  get isDebug() {
    return process.env.REACT_APP_DEBUG_MODE === 'true';
  },
  get isProduction() {
    return !this.isDevelopment && !this.isDebug;
  },
};

class ApiClient {
  constructor(baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001') {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };

    // Initialize userId from localStorage or default
    this.userId = localStorage.getItem('user_id') || 'dev-user-123';

    log('API Client initialized:', {
      baseURL: this.baseURL,
      userId: this.userId
    });
  }

  /**
   * Get current operational mode
   */
  getMode() {
    if (MODE.isDevelopment) return 'development';
    if (MODE.isDebug) return 'debug';
    return 'production';
  }

  /**
   * Set user ID for development mode
   */
  setUserId(userId) {
    this.userId = userId;
    localStorage.setItem('user_id', userId);
    log('User ID set to:', userId);
  }

  /**
   * Get authentication headers
   */
  async getAuthHeaders() {
    const headers = {};

    if (MODE.isDevelopment) {
      headers['X-User-Id'] = this.userId;
      log('Using dev auth header:', headers);
      return headers;
    }

    // Production/Debug mode — send JWT Bearer token
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      log('Failed to get auth session:', error.message);
    }

    log('Using auth headers:', Object.keys(headers));
    return headers;
  }

  /**
   * Refresh authentication token
   */
  async refreshToken() {
    if (MODE.isDevelopment) return;
    await fetchAuthSession({ forceRefresh: true });
  }

  /**
   * Set base URL for all requests
   */
  setBaseURL(url) {
    this.baseURL = url;
    log('Base URL updated to:', url);
  }

  /**
   * Set default headers for all requests
   */
  setDefaultHeaders(headers) {
    this.defaultHeaders = { ...this.defaultHeaders, ...headers };
    log('Default headers updated:', this.defaultHeaders);
  }

  /**
   * Generic request method with automatic auth and retry
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    try {
      // Get auth headers
      const authHeaders = await this.getAuthHeaders();

      const config = {
        headers: {
          ...this.defaultHeaders,
          ...authHeaders,
          ...options.headers
        },
        ...options,
      };

      log(`${config.method || 'GET'} ${url}`);
      if (config.body && DEBUG) {
        try {
          const bodyData = JSON.parse(config.body);
          log('Request body:', bodyData);
        } catch (e) {
          log('Request body (non-JSON):', config.body?.substring(0, 100) + '...');
        }
      }

      const response = await fetch(url, config);

      log(`Response: ${response.status} ${response.statusText}`);

      // Handle 401 Unauthorized - token expired
      if (response.status === 401 && !MODE.isDevelopment) {
        log('Received 401, attempting token refresh...');

        try {
          await this.refreshToken();

          // Retry request with new token
          const newAuthHeaders = await this.getAuthHeaders();
          config.headers = {
            ...config.headers,
            ...newAuthHeaders,
          };

          log('Retrying request with refreshed token...');
          return await this.request(endpoint, options);
        } catch (refreshError) {
          log('Token refresh failed, redirecting to login');
          this.handleAuthenticationFailure();
          throw new Error('Session expired. Please log in again.');
        }
      }

      // Handle 403 Forbidden - insufficient permissions
      if (response.status === 403) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }

        const error = new Error(errorData.message || 'Access denied');
        error.status = 403;
        error.code = 'FORBIDDEN';
        error.data = errorData;
        throw error;
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${errorText}`;
        let errorData = null;

        // Try to parse error response as JSON
        try {
          errorData = JSON.parse(errorText);

          // Handle validation errors with detailed field information
          if (errorData.error_code === 'BAD_REQUEST' && errorData.message) {
            errorMessage = errorData.message;

            // Try to extract field-specific validation errors for better UX
            const validationMatch = errorData.message.match(/\[({.*})\]/);
            if (validationMatch) {
              try {
                const validationError = JSON.parse(validationMatch[1].replace(/'/g, '"'));
                errorMessage = `Validation error: ${validationError.field} - ${validationError.message}`;
              } catch (e) {
                // Keep the original message if parsing fails
              }
            }
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // Not JSON, use raw text
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        error.response = response;
        error.data = errorData;
        throw error;
      }

      const contentType = response.headers.get('content-type');
      let data;

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
        log('Response data:', data);
      } else {
        data = await response.text();
        log('Response text length:', data.length);
      }

      return {
        data,
        status: response.status,
        headers: response.headers,
        ok: response.ok,
      };

    } catch (error) {
      log('Request failed:', error.message);

      // Handle authentication errors
      if (error.code === 'AUTH_REQUIRED') {
        this.handleAuthenticationFailure();
      }

      // Enhance error with more context
      if (!error.status) {
        error.message = `Network error: ${error.message}`;
      }

      // Show error to user via global handler
      if (globalErrorHandler) {
        globalErrorHandler(error.message);
      }

      throw error;
    }
  }

  /**
   * Handle authentication failure (redirect to login)
   */
  handleAuthenticationFailure() {
    if (!MODE.isDevelopment) {
      // Store current location to redirect back after login
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      // Redirect to login page
      window.location.href = '/login';
    }
  }

  /**
   * GET request
   */
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    return this.request(url, {
      method: 'GET',
    });
  }

  /**
   * POST request
   */
  async post(endpoint, data = null, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : null,
      ...options,
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data = null, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : null,
      ...options,
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      ...options,
    });
  }

  // ==================== Story API Methods ====================

  /**
   * Create a new story
   */
  async createStory(storyData) {
    const response = await this.post('/stories', storyData);
    return response.data;
  }

  /**
   * Get a specific story by ID
   */
  async getStory(storyId) {
    const response = await this.get(`/stories/${storyId}`);
    return response.data;
  }

  /**
   * List all stories for the user
   */
  async listStories(pageSize = 20, nextToken = null) {
    const params = { page_size: pageSize };
    if (nextToken) {
      params.next_token = nextToken;
    }
    const response = await this.get('/stories', params);
    return response.data;
  }

  /**
   * Check story status (for polling)
   */
  async checkStoryStatus(storyId) {
    const response = await this.get(`/stories/${storyId}/status`);
    return response.data;
  }

}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;
export { ApiClient, MODE };
